import React, { FC, useEffect } from 'react';
import { Col, Row, Skeleton } from 'antd';
import { InfoTooltip } from 'common/components/InfoTooltip';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesSummaryModel } from 'app/store/reducers/monitorSales.reducer';
import { IMonitorSalesParams } from 'entities/MonitorSales/MonitorSales.models';
import { SummaryRow } from 'entities/MonitorSales/components/SummaryRow';
import { SummaryTrendRow } from 'entities/MonitorSales/components/SummaryTrendRow';
import { ChangeArrow } from 'entities/MonitorSales/components/ChangeArrow';

interface IComponentProps {
  params?: IMonitorSalesParams;
  dateRange: string;
}

export const SalesSummaryBlock: FC<IComponentProps> = ({ params, dateRange }) => {
  const dispatch = useAppDispatch();
  const { monitorSalesSummaryModel, monitorSalesSummaryModelLoading } = useAppSelector((state) => state.monitorSales);
  const salesSummaryData = monitorSalesSummaryModel;

  useEffect(() => {
    dispatch(getMonitorSalesSummaryModel(params));
  }, [params?.storeCode, params?.week]);

  return (
    <div className="ms-block">
      <div className="ms__header">
        <span className="ff-medium fs-xs">{dateRange}</span>
        <InfoTooltip
          title={
            <div>
              <div className="mb-3 ff-medium fs-xxs">Summary</div>
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">Track four goals at once!</div>
              <Row className="mb-4">
                <Col span={4} className="ff-regular fs-xxxs">
                  Sales
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={18} className="ff-regular fs-xxxs">
                  Total Sales
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={4} className="ff-regular fs-xxxs">
                  TC
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={18} className="ff-regular fs-xxxs">
                  Transaction Count
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={4} className="ff-regular fs-xxxs">
                  AC
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={18} className="ff-regular fs-xxxs">
                  Average Check
                </Col>
              </Row>
              <Row className="mb-4">
                <Col span={4} className="ff-regular fs-xxxs">
                  ADS
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={18} className="ff-regular fs-xxxs">
                  Average Daily Sales
                </Col>
              </Row>
              <div className="mb-3 ff-regular fs-xxxs color-gray-1">
                See how you’re doing now versus LP (last period) & LY (last year) and your trend:
              </div>
              <Row className="mb-4">
                <Col span={6} className="ff-regular fs-xxxs color-green">
                  Green <ChangeArrow up={true} />
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  increased
                </Col>
              </Row>
              <Row>
                <Col span={6} className="ff-regular fs-xxxs color-red">
                  Red <ChangeArrow up={false} />
                </Col>
                <Col span={2} className="ff-regular fs-xxxs color-gray-1">
                  =
                </Col>
                <Col span={16} className="ff-regular fs-xxxs">
                  declined
                </Col>
              </Row>
            </div>
          }
        />
      </div>

      <div className="ms__body">
        {monitorSalesSummaryModelLoading ? (
          <Skeleton active paragraph={{ rows: 4 }} title={false} />
        ) : (
          <Row gutter={36}>
            <Col span={24} xxl={16}>
              <Row align="middle" gutter={20} className="mb-4">
                <Col span={18} className="ff-medium">
                  Targets
                </Col>
                <Col span={3} className="ff-medium t-align-e">
                  vs LP
                </Col>
                <Col span={3} className="ff-medium t-align-e">
                  vs LY
                </Col>
              </Row>

              <SummaryRow label="Sales" metric={salesSummaryData?.sales} />
              <SummaryRow label="TC" metric={salesSummaryData?.transactionCount} />
              <SummaryRow label="AC" metric={salesSummaryData?.averageCheck} />
              <SummaryRow label="ADS" metric={salesSummaryData?.averageDailySales} />
            </Col>

            <Col span={24} xxl={8}>
              <Row align="middle" gutter={20} className="mb-4">
                <Col className="ff-medium t-align-e">Trend</Col>
              </Row>

              <SummaryTrendRow data={salesSummaryData?.sales.trend} label="Sales" />
              <SummaryTrendRow data={salesSummaryData?.transactionCount.trend} label="TC" />
              <SummaryTrendRow data={salesSummaryData?.averageCheck.trend} label="AC" />
              <SummaryTrendRow data={salesSummaryData?.averageDailySales.trend} label="ADS" />
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
